import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Loader2, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import type { Factor } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppSectionHeader,
  AppText,
} from '../../../ui-foundation';

type EnrolledTotp = {
  factorId: string;
  qrCode: string;
  secret: string;
};

type MfaSettingsCardProps = {
  enforced?: boolean;
  enforcedReason?: string;
};

function ensureQrDataUrl(value: string) {
  return value.startsWith('data:') ? value : `data:image/svg+xml;utf-8,${value}`;
}

export default function MfaSettingsCard({ enforced = false, enforcedReason }: MfaSettingsCardProps) {
  const { user, role, mfaLevel, requiresMfa, refreshSecurity } = useApp();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [setupCode, setSetupCode] = useState('');
  const [enrollment, setEnrollment] = useState<EnrolledTotp | null>(null);
  const [verifiedFactors, setVerifiedFactors] = useState<Factor[]>([]);
  const [unverifiedFactors, setUnverifiedFactors] = useState<Factor[]>([]);
  const [enrollmentUnavailable, setEnrollmentUnavailable] = useState(false);

  const loadFactors = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.auth.mfa.listFactors();

    if (error) {
      console.error(error);
      toast.error('Could not load two-step verification settings.');
    } else {
      setVerifiedFactors(data.totp.filter((factor) => factor.status === 'verified'));
      setUnverifiedFactors(data.totp.filter((factor) => factor.status !== 'verified'));
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    void loadFactors();
  }, [loadFactors]);

  const hasVerifiedFactor = verifiedFactors.length > 0;
  const activeFactor = verifiedFactors[0] || null;
  const mustStayOn = enforced || role === 'admin' || role === 'moderator';

  const startSetup = async () => {
    if (!user) return;

    setStarting(true);
    setEnrollmentUnavailable(false);

    for (const factor of unverifiedFactors) {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Adulting authenticator',
    });

    if (error || !data || data.type !== 'totp') {
      console.error(error);
      if (error?.message?.includes('MFA enroll is disabled for TOTP')) {
        setEnrollmentUnavailable(true);
        toast.error('Two-step setup is currently unavailable on Adulting. You can still use the app, but admin tools stay locked until this is fixed.');
      } else {
        toast.error(error?.message || 'Could not start two-step verification.');
      }
    } else {
      setEnrollment({
        factorId: data.id,
        qrCode: ensureQrDataUrl(data.totp.qr_code),
        secret: data.totp.secret,
      });
      toast.success('Scan the QR code, then enter the 6-digit code from your authenticator app.');
    }

    setStarting(false);
  };

  const verifySetup = async () => {
    if (!enrollment) return;

    const code = setupCode.trim();
    if (!code) {
      toast.error('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enrollment.factorId,
      code,
    });

    if (error) {
      console.error(error);
      toast.error(error.message || 'Could not verify that authenticator code.');
    } else {
      setEnrollment(null);
      setSetupCode('');
      toast.success('Two-step verification is now on.');
      await Promise.all([loadFactors(), refreshSecurity()]);
    }

    setVerifying(false);
  };

  const disableMfa = async () => {
    if (!activeFactor) return;
    if (mfaLevel !== 'aal2') {
      toast.error('Sign in with your authenticator code before turning two-step verification off.');
      return;
    }

    setDisabling(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId: activeFactor.id });

    if (error) {
      console.error(error);
      toast.error(error.message || 'Could not turn off two-step verification.');
    } else {
      toast.success('Two-step verification is now off.');
      await Promise.all([loadFactors(), refreshSecurity()]);
    }

    setDisabling(false);
  };

  const copySecret = async () => {
    if (!enrollment?.secret) return;
    await navigator.clipboard.writeText(enrollment.secret);
    toast.success('Authenticator secret copied.');
  };

  const statusLabel = useMemo(() => {
    if (loading) return 'Checking security…';
    if (mustStayOn && !hasVerifiedFactor) return 'Required before privileged access is unlocked';
    if (requiresMfa) return 'Second step required before app access';
    if (hasVerifiedFactor) return 'Two-step verification is on';
    return 'Two-step verification is off';
  }, [hasVerifiedFactor, loading, mustStayOn, requiresMfa]);

  return (
    <AppCard gap="$md">
      <AppCardHeader>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <AppSectionHeader
              title="Two-step verification"
              subtitle="Add an authenticator app step on top of your password. Adulting uses your Supabase account for this, so the code is checked before app access."
            />
            {mustStayOn ? (
              <AppText fontSize="$sm" color="$primary">
                {enforcedReason || 'Because this account can access admin or moderation tools, two-step verification must stay on.'}
              </AppText>
            ) : null}
            <AppText fontSize="$sm" color="$colorMuted">
              {statusLabel}
            </AppText>
          </div>
        </div>
      </AppCardHeader>

      {enrollmentUnavailable ? (
        <AppInlineAlert title="Authenticator setup is temporarily unavailable on Adulting." tone="warning">
          The app is still usable, but admin and moderation tools will stay locked until the Adulting Supabase MFA setting starts accepting TOTP enrollment.
        </AppInlineAlert>
      ) : null}

      <AppCardBody>
        {hasVerifiedFactor ? (
          <AppCard backgroundColor="$background" px="$md" py="$md" gap="$sm">
            <div className="flex items-center gap-2">
              <Smartphone size={15} className="text-primary" />
              <AppText fontSize="$md" fontWeight="700">
                {activeFactor?.friendly_name || 'Authenticator app connected'}
              </AppText>
            </div>
            <AppText fontSize="$sm" color="$colorMuted">
              You will be asked for a 6-digit authenticator code after password sign-in.
            </AppText>
            {mustStayOn ? (
              <AppInlineAlert title="Two-step verification is locked on for privileged accounts.">
                Remove admin or moderator access before turning it off.
              </AppInlineAlert>
            ) : (
              <AppButton type="button" tone="outline" onPress={disableMfa} disabled={disabling} alignSelf="flex-start">
                {disabling ? <Loader2 className="animate-spin" /> : <ShieldOff />}
                {disabling ? 'Turning off…' : 'Turn off two-step verification'}
              </AppButton>
            )}
          </AppCard>
        ) : (
          <div className="space-y-3">
            {!enrollment ? (
              <AppButton type="button" onPress={startSetup} disabled={starting} alignSelf="flex-start">
                {starting ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
                {starting ? 'Starting…' : 'Set up with an authenticator app'}
              </AppButton>
            ) : (
              <AppCard backgroundColor="$background" px="$md" py="$md" gap="$md">
                <AppFieldGroup label="1. Scan the QR code">
                  <img src={enrollment.qrCode} alt="Authenticator QR code" className="h-44 w-44 rounded-xl border border-border bg-white p-2" />
                </AppFieldGroup>

                <AppFieldGroup label="2. Or enter this secret manually">
                  <div className="flex gap-2">
                    <AppInput readOnly value={enrollment.secret} flex={1} fontFamily="monospace" fontSize="$sm" />
                    <AppButton type="button" tone="outline" onPress={copySecret}>
                      <Copy size={16} />
                    </AppButton>
                  </div>
                </AppFieldGroup>

                <AppFieldGroup label="3. Enter the 6-digit code">
                  <AppInput
                    value={setupCode}
                    onChange={(event) => setSetupCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="123456"
                    width={180}
                  />
                </AppFieldGroup>

                <AppButtonRow>
                  <AppButton type="button" onPress={verifySetup} disabled={verifying}>
                    {verifying ? <Loader2 className="animate-spin" /> : <ShieldCheck />}
                    {verifying ? 'Checking…' : 'Verify and turn on'}
                  </AppButton>
                  <AppButton
                    type="button"
                    tone="outline"
                    onPress={() => {
                      setEnrollment(null);
                      setSetupCode('');
                    }}
                  >
                    Cancel
                  </AppButton>
                </AppButtonRow>
              </AppCard>
            )}
          </div>
        )}
      </AppCardBody>
    </AppCard>
  );
}
